'use client';

import { useState, useRef, useEffect } from 'react';
import { cn } from '@/core/utils/cn';

interface DropdownItem {
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  danger?: boolean;
  divider?: boolean;
}

interface DropdownMenuProps {
  id?: string;
  trigger: React.ReactNode;
  items: DropdownItem[];
  align?: 'left' | 'right';
  className?: string;
}

export function DropdownMenu({ id, trigger, items, align = 'right', className }: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div id={id} ref={ref} className={cn('relative inline-flex', className)}>
      <div onClick={() => setOpen(!open)}>{trigger}</div>
      {open && (
        <div className={cn(
          'absolute top-full mt-1 z-50 min-w-0 sm:min-w-[180px] max-w-[calc(100vw-2rem)] bg-hms-card border border-hms-border rounded-xl shadow-2xl py-1',
          align === 'right' ? 'right-0' : 'left-0'
        )}>
          {items.map((item, i) => (
            item.divider ? (
              <div key={i} className="h-px bg-hms-border my-1" />
            ) : (
              <button
                key={i}
                id={id ? `${id}-item-${i}` : undefined}
                onClick={() => { item.onClick?.(); setOpen(false); }}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 text-[13px] transition-colors text-left',
                  item.danger
                    ? 'text-hms-red hover:bg-hms-red/5'
                    : 'text-hms-textSub hover:text-hms-text hover:bg-white/5'
                )}
              >
                {item.icon}
                {item.label}
              </button>
            )
          ))}
        </div>
      )}
    </div>
  );
}
